// Product types slider on collection pages (sections/collection-types-slider.liquid).
// Slides exactly like the homepage categories carousel: GemPages runs it with
// Swiper, so this uses Swiper (assets/swiper.min.js) with the same options,
// read from the live homepage: 500ms "ease" slides, arrows move one card,
// drag with the mouse or finger and release onto the nearest card (a quick
// flick moves one card), resistance past the first and last card.
if (!customElements.get('collection-types-slider')) {
  class CollectionTypesSlider extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-cs-viewport]');
      this.list = this.querySelector('[data-cs-track]');
      this.prevButton = this.querySelector('[data-cs-prev]');
      this.nextButton = this.querySelector('[data-cs-next]');
      if (!this.viewport || !this.list) return;

      this.fitTitles();
      // Arimo changes where the names wrap once it loads.
      if (document.fonts) document.fonts.ready.then(() => this.fitTitles());
      if (typeof Swiper === 'undefined') return;

      this.swiper = new Swiper(this.viewport, {
        wrapperClass: 'cs-track',
        slideClass: 'cs-item',
        speed: 500,
        watchSlidesProgress: true,
        loop: false,
        slidesPerView: 8,
        breakpoints: {
          0: { slidesPerView: 3.65, spaceBetween: 0 },
          768: { slidesPerView: 7, spaceBetween: 0 },
          1025: { slidesPerView: 8, spaceBetween: 10 },
        },
        on: {
          init: () => {
            this.updateArrows();
            this.placeArrows();
          },
          progress: () => this.updateArrows(),
          slideChange: () => this.updateArrows(),
          lock: () => this.updateArrows(),
          unlock: () => this.updateArrows(),
          resize: () => {
            this.fitTitles();
            this.updateArrows();
            this.placeArrows();
          },
          breakpoint: () => {
            this.fitTitles();
            this.updateArrows();
            this.placeArrows();
          },
        },
      });

      this.onPrev = () => this.swiper.slidePrev();
      this.onNext = () => this.swiper.slideNext();
      if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);

      // page-collection-types.js removes and reorders the cards once Boost
      // answers: recalculate the slides.
      this.observer = new MutationObserver(() => {
        this.swiper.update();
        this.fitTitles();
        this.placeArrows();
      });
      this.observer.observe(this.list, { childList: true });
    }

    disconnectedCallback() {
      if (!this.swiper) return;
      if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
      this.observer.disconnect();
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    // Arrows are hidden at each end (CSS hides disabled arrows); with every
    // card in view (Swiper locks), the row is centered and has no arrows.
    updateArrows() {
      const swiper = this.swiper || (this.viewport && this.viewport.swiper);
      if (!swiper) return;
      this.toggleAttribute('data-no-overflow', swiper.isLocked);
      if (this.prevButton) this.prevButton.disabled = swiper.isBeginning;
      if (this.nextButton) this.nextButton.disabled = swiper.isEnd;
    }

    // Names on one line keep the homepage line height (180%); names that wrap
    // to two or more lines get the tighter cs-title--multiline (1.2). Changing
    // the line height doesn't change where a name wraps, so this is stable.
    fitTitles() {
      const titles = Array.from(this.querySelectorAll('.cs-title'));
      titles.forEach((title) => title.classList.remove('cs-title--multiline'));
      if (!titles.length) return;
      this.lineHeight = parseFloat(getComputedStyle(titles[0]).lineHeight) || 0;
      titles.forEach((title) => {
        if (title.offsetHeight > this.lineHeight * 1.5) title.classList.add('cs-title--multiline');
      });
      this.placeArrows();
    }

    // Arrows centered on a card with a one-line name (circle + gap + one
    // line), like the homepage, where every name fits on one line. Centering
    // on the whole row would push them down when some name wraps to two lines.
    placeArrows() {
      const card = this.querySelector('.cs-card');
      const media = card && card.querySelector('.cs-media');
      if (!media) return;
      const gap = parseFloat(getComputedStyle(card).rowGap) || 0;
      const line = this.lineHeight || 0;
      const top = (media.getBoundingClientRect().height + gap + line) / 2;
      [this.prevButton, this.nextButton].forEach((button) => {
        if (button) button.style.top = `${top}px`;
      });
    }
  }

  customElements.define('collection-types-slider', CollectionTypesSlider);
}
